import { SearchOutlined } from '@ant-design/icons';
import { Avatar, Empty, Input, Table, Tag } from 'antd';
import cn from 'classnames';
import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { BASE_URI } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { organizationConfig, organizationModel } from '@/entities/organization';

import { organizationStaffMessages as messages } from '../../config';

import styles from './OrganizationStaff.module.scss';

const getInitials = (firstName?: string, lastName?: string) => {
  const first = firstName?.trim()?.[0] ?? '';
  const last = lastName?.trim()?.[0] ?? '';

  return `${first}${last}`.toUpperCase();
};

const getFullName = (firstName?: string, lastName?: string) =>
  `${firstName ?? ''} ${lastName ?? ''}`.trim();

const getUserAvatarSrc = (userID: string) =>
  `${BASE_URI}/api/storage/download/user_avatar/thumbnail/${userID}`;

type Props = {
  className?: string;
};

export const OrganizationStaff: FC<Props> = (props) => {
  const { className } = props;

  const { formatMessage } = useIntl();
  const staff = useAppSelector(
    organizationModel.selectors.selectSortedOrganizationStaff,
  );
  const loadingState = useAppSelector(
    organizationModel.selectors.selectOrganizationLoading,
  );

  const [search, setSearch] = useState('');

  const enrichedStaff = useMemo(
    () =>
      staff.map((member) => {
        const fullName = getFullName(
          member.PersonalData?.FirstName,
          member.PersonalData?.LastName,
        );
        const email = member.PersonalData?.Email ?? '';
        const specializations = member.JobPositions.map((position) =>
          formatMessage(organizationConfig.specialityType[position]),
        );
        const roleLabel = formatMessage(
          organizationConfig.staffRoleMessages[member.Role],
        );
        const isDeleted = member.Status?.case === 'Deleted';

        return {
          member,
          fullName,
          email,
          specializations,
          roleLabel,
          isDeleted,
        };
      }),
    [staff, formatMessage],
  );

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return enrichedStaff;
    }

    return enrichedStaff.filter(
      ({ fullName, email, specializations, roleLabel }) =>
        fullName.toLowerCase().includes(query) ||
        email.toLowerCase().includes(query) ||
        specializations.join(' ').toLowerCase().includes(query) ||
        roleLabel.toLowerCase().includes(query),
    );
  }, [enrichedStaff, search]);

  type Row = (typeof enrichedStaff)[number];

  const columns = [
    {
      title: formatMessage(messages.colName),
      key: 'name',
      render: (_: unknown, row: Row) => {
        const initials = getInitials(
          row.member.PersonalData?.FirstName,
          row.member.PersonalData?.LastName,
        );
        const avatarSrc = row.member.UserID
          ? getUserAvatarSrc(row.member.UserID)
          : undefined;

        return (
          <div className={styles.nameCell}>
            <Avatar size="small" src={avatarSrc}>
              {initials || '?'}
            </Avatar>
            <span>{row.fullName || '—'}</span>
          </div>
        );
      },
    },
    {
      title: formatMessage(messages.colEmail),
      key: 'email',
      render: (_: unknown, row: Row) => row.email || '—',
    },
    {
      title: formatMessage(messages.colSpecialization),
      key: 'specialization',
      render: (_: unknown, row: Row) => {
        if (!row.specializations.length) {
          return '—';
        }

        return (
          <div className={styles.listColumn}>
            {row.specializations.map((specialization) => (
              <span key={specialization}>{specialization}</span>
            ))}
          </div>
        );
      },
    },
    {
      title: formatMessage(messages.colAccessLevel),
      key: 'accessLevel',
      render: (_: unknown, row: Row) => row.roleLabel || '—',
    },
    {
      title: formatMessage(messages.colRegistration),
      key: 'registration',
      render: (_: unknown, row: Row) =>
        row.member.UserID ? (
          <Tag color="green">{formatMessage(messages.registered)}</Tag>
        ) : (
          <Tag color="default">{formatMessage(messages.notRegistered)}</Tag>
        ),
    },
    {
      title: formatMessage(messages.colStatus),
      key: 'status',
      render: (_: unknown, row: Row) =>
        row.isDeleted ? (
          <Tag color="default">{formatMessage(messages.statusDeleted)}</Tag>
        ) : (
          <Tag color="green">{formatMessage(messages.statusActive)}</Tag>
        ),
    },
  ];

  return (
    <div className={cn(styles.container, className)}>
      <Input
        className={styles.search}
        value={search}
        placeholder={formatMessage(messages.searchPlaceholder)}
        prefix={<SearchOutlined rev={undefined} />}
        allowClear
        onChange={(event) => setSearch(event.currentTarget.value)}
      />

      <Table<Row>
        rowKey={(row) => row.member.ID}
        size="small"
        loading={loadingState === 'pending'}
        columns={columns}
        dataSource={filteredStaff}
        pagination={false}
        rowClassName={(row) => (row.isDeleted ? styles.deletedRow : '')}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={formatMessage(messages.noStaff)}
            />
          ),
        }}
      />
    </div>
  );
};
